import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/Button";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { SetupStep } from "@/components/SetupStep";
import { TextInput } from "@/components/TextInput";
import { useApi } from "@/lib/api";
import { isEnabled } from "@/lib/enums";
import { formatDateTime } from "@/lib/formatDateTime";
import { PAGE_SIZE } from "@/lib/pagination";
import type { ScimStatus } from "./shared";

interface GoogleDirectoryStatus {
  adminEmail: string;
  groupIds: string[];
  lastSyncedAt: string | null;
  lastSyncError: string | null;
}

interface GoogleGroup {
  id: string;
  name: string;
  email: string;
}

export function GoogleDirectorySteps({
  orgId,
  ssoEnabled,
  scimStatus,
  submitting,
  onToggleEnabled,
  onChanged,
  onError,
  onSuccess,
}: {
  orgId: string;
  ssoEnabled: boolean;
  scimStatus: ScimStatus | null;
  submitting: boolean;
  onToggleEnabled: () => void;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
  onSuccess: (message: string) => void;
}) {
  const api = useApi();
  const [status, setStatus] = useState<GoogleDirectoryStatus | null>(null);
  const [groups, setGroups] = useState<GoogleGroup[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [serviceAccountKey, setServiceAccountKey] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [editingCredentials, setEditingCredentials] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  const connected = status !== null;

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<GoogleDirectoryStatus>(
        `/organizations/${orgId}/scim/google`,
      );
      setStatus(data);
      setSelectedIds(data?.groupIds ?? []);
    } catch {
      setStatus(null);
    }
  }, [api, orgId]);

  useEffect(() => {
    if (ssoEnabled) {
      // oxlint-disable-next-line react/set-state-in-effect
      void loadStatus();
    }
  }, [ssoEnabled, loadStatus, scimStatus]);

  useEffect(() => {
    if (!connected) {
      return;
    }

    void api
      .get<GoogleGroup[]>(`/organizations/${orgId}/scim/google/groups`)
      .then(setGroups)
      .catch((err: unknown) =>
        onError(
          err instanceof Error
            ? err.message
            : "Couldn't load Google Workspace groups.",
        ),
      );
  }, [api, orgId, connected, onError]);

  const filteredGroups = useMemo(() => {
    const filter = nameFilter.trim().toLowerCase();

    return (groups ?? []).filter(
      (group) =>
        !filter ||
        group.name.toLowerCase().includes(filter) ||
        group.email.toLowerCase().includes(filter),
    );
  }, [groups, nameFilter]);

  const selections = useMemo(
    () => Object.fromEntries((groups ?? []).map((g) => [g.id, g])),
    [groups],
  );

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();

    setSaving(true);
    onError(null);

    try {
      await api.send(
        `/organizations/${orgId}/scim/google`,
        "POST",
        { serviceAccountKey, adminEmail },
        "Couldn't connect Google Workspace.",
      );

      setServiceAccountKey("");
      setEditingCredentials(false);
      await loadStatus();
      await onChanged();
      onSuccess("Google Workspace connected.");
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : "Couldn't connect Google Workspace.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveGroups() {
    setSaving(true);
    onError(null);

    try {
      await api.send(
        `/organizations/${orgId}/scim/google/groups`,
        "PUT",
        { groupIds: selectedIds },
        "Couldn't save the groups.",
      );

      await loadStatus();
      onSuccess("Groups saved.");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Couldn't save the groups.");
    } finally {
      setSaving(false);
    }
  }

  function toggleGroup(group: GoogleGroup) {
    setSelectedIds((current) =>
      current.includes(group.id)
        ? current.filter((id) => id !== group.id)
        : [...current, group.id],
    );
  }

  const groupsSaved =
    connected &&
    status.groupIds.length > 0 &&
    status.groupIds.length === selectedIds.length &&
    status.groupIds.every((id) => selectedIds.includes(id));

  return (
    <>
      <SetupStep
        label="2"
        state={!ssoEnabled ? "todo" : connected ? "done" : "current"}
        title="Connect Google Workspace"
      >
        {!ssoEnabled ? (
          <p className="mt-0.5 text-sm text-foreground/70">
            Connect with a service account that has domain-wide delegation.
          </p>
        ) : connected && !editingCredentials ? (
          <div className="mt-0.5 flex flex-wrap items-center gap-3 text-sm text-foreground/70">
            <span>Reading the directory as {status.adminEmail}</span>
            <Button
              variant="secondary"
              onClick={() => {
                setAdminEmail(status.adminEmail);
                setEditingCredentials(true);
              }}
            >
              Replace key
            </Button>
          </div>
        ) : (
          <form onSubmit={handleConnect} className="mt-2 space-y-4">
            <div>
              <label
                htmlFor="googleServiceAccountKey"
                className="mb-1.5 block text-sm text-foreground"
              >
                Service account key
              </label>
              <textarea
                id="googleServiceAccountKey"
                value={serviceAccountKey}
                onChange={(e) => setServiceAccountKey(e.target.value)}
                placeholder='{ "type": "service_account", ... }'
                required
                rows={4}
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </div>
            <div>
              <label
                htmlFor="googleAdminEmail"
                className="block text-sm text-foreground"
              >
                Administrator email
              </label>
              <p
                id="googleAdminEmailHint"
                className="mt-0.5 mb-1.5 text-xs text-foreground/70"
              >
                Google only allows directory access on behalf of an
                administrator. Use one who can view users and groups.
              </p>
              <TextInput
                type="email"
                id="googleAdminEmail"
                aria-describedby="googleAdminEmailHint"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Connecting..." : "Connect"}
              </Button>
              {editingCredentials && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setEditingCredentials(false)}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        )}
      </SetupStep>

      <SetupStep
        label="3"
        state={!connected ? "todo" : groupsSaved ? "done" : "current"}
        title="Choose the groups to sync"
      >
        {!connected ? (
          <p className="mt-0.5 text-sm text-foreground/70">
            Each group becomes a team, and only its members are added.
          </p>
        ) : (
          <div className="mt-2 space-y-3">
            <EntityMultiSelect
              label="Groups"
              searchLabel="Search groups by name or email"
              searchPlaceholder="Search group name or email"
              selectedIds={selectedIds}
              options={
                groups &&
                filteredGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
              }
              emptyMessage="No groups match your search."
              nameFilter={nameFilter}
              onNameFilterChange={(value) => {
                setNameFilter(value);
                setPage(1);
              }}
              page={page}
              totalPages={Math.ceil(filteredGroups.length / PAGE_SIZE)}
              onPageChange={setPage}
              onToggle={toggleGroup}
              selections={selections}
              renderLabel={(group) => `${group.name} (${group.email})`}
            />
            <Button
              variant="primary"
              onClick={handleSaveGroups}
              disabled={saving || selectedIds.length === 0 || groupsSaved}
            >
              {saving ? "Saving..." : "Save groups"}
            </Button>
          </div>
        )}
      </SetupStep>

      <SetupStep
        label="4"
        state={
          isEnabled(scimStatus?.status ?? 0)
            ? "done"
            : groupsSaved
              ? "current"
              : "todo"
        }
        title="Enable directory sync"
        last
      >
        <p className="mt-0.5 text-sm text-foreground/70">
          {status?.lastSyncError
            ? `The last sync failed: ${status.lastSyncError}`
            : status?.lastSyncedAt
              ? `Last synced ${formatDateTime(status.lastSyncedAt)}. Tendra syncs every 30 minutes.`
              : "Tendra syncs within a minute of enabling, then every 30 minutes."}
        </p>
        {groupsSaved && (
          <div className="mt-4 flex flex-wrap gap-3 border-t border-muted pt-4">
            <Button
              variant="secondary"
              onClick={onToggleEnabled}
              disabled={submitting}
            >
              {isEnabled(scimStatus?.status ?? 0)
                ? "Disable directory sync"
                : "Enable directory sync"}
            </Button>
          </div>
        )}
      </SetupStep>
    </>
  );
}
